/**
 * SupplierCategoriesCard — multi-select category chips with inline-add.
 *
 * Reads the configurable supplier_category taxonomy and the supplier's
 * current selection. Operators can toggle chips or type a new value that
 * gets persisted to both the taxonomy and the supplier's set.
 */
import { useState, useMemo, useEffect } from 'react';
import { Loader2, Plus, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';
import {
  useSupplierCategories,
  useTaxonomy,
  useSupplierEnrichmentMutations,
} from '../hooks/useSupplierEnrichment';

interface Props {
  supplierId: string;
  /** Block writes for non-admins. Reads are always allowed. */
  canEdit: boolean;
}

export function SupplierCategoriesCard({ supplierId, canEdit }: Props) {
  const categoriesQuery = useSupplierCategories(supplierId);
  const taxonomyQuery = useTaxonomy('supplier_category');
  const { setCategories, addTaxonomyValue } = useSupplierEnrichmentMutations();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [newValue, setNewValue] = useState('');

  // Sync local selection with server data when it loads/changes.
  useEffect(() => {
    if (categoriesQuery.data) {
      setSelected(new Set(categoriesQuery.data.map((c) => c.category)));
    }
  }, [categoriesQuery.data]);

  const taxonomyValues = useMemo(
    () => (taxonomyQuery.data ?? []).map((t) => t.value),
    [taxonomyQuery.data],
  );

  const allChips = useMemo(() => {
    const set = new Set(taxonomyValues);
    for (const v of selected) set.add(v); // include any current selections not in taxonomy
    return Array.from(set).sort();
  }, [taxonomyValues, selected]);

  const toggle = async (value: string) => {
    if (!canEdit || setCategories.isPending) return;
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setSelected(next);
    try {
      await setCategories.mutateAsync({ supplierId, categories: Array.from(next) });
    } catch (err) {
      toast({
        title: 'Failed to save categories',
        description: err instanceof Error ? err.message : 'Try again.',
        variant: 'destructive',
      });
      // Roll back local state if server rejected.
      if (categoriesQuery.data) {
        setSelected(new Set(categoriesQuery.data.map((c) => c.category)));
      }
    }
  };

  const handleAddNew = async () => {
    const value = newValue.trim();
    if (!value) return;
    try {
      await addTaxonomyValue.mutateAsync({ kind: 'supplier_category', value });
      const next = new Set(selected);
      next.add(value);
      setSelected(next);
      await setCategories.mutateAsync({ supplierId, categories: Array.from(next) });
      setNewValue('');
    } catch (err) {
      toast({
        title: 'Failed to add category',
        description: err instanceof Error ? err.message : 'Try again.',
        variant: 'destructive',
      });
    }
  };

  const isLoading = categoriesQuery.isLoading || taxonomyQuery.isLoading;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Categories</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              {allChips.map((value) => {
                const isOn = selected.has(value);
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => toggle(value)}
                    disabled={!canEdit || setCategories.isPending}
                    className={
                      'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition ' +
                      (isOn
                        ? 'bg-primary/10 border-primary/40 text-primary'
                        : 'bg-background text-muted-foreground hover:text-foreground') +
                      (canEdit ? '' : ' cursor-default opacity-80')
                    }
                  >
                    {value}
                    {isOn && canEdit && <X className="h-3 w-3" />}
                  </button>
                );
              })}
              {allChips.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No categories yet. Add one below.
                </p>
              )}
            </div>

            {canEdit && (
              <div className="flex items-center gap-2 pt-2">
                <Input
                  placeholder="Add custom category (e.g. dye-house)"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddNew();
                    }
                  }}
                  className="h-8 text-xs"
                  disabled={addTaxonomyValue.isPending}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleAddNew}
                  disabled={!newValue.trim() || addTaxonomyValue.isPending}
                  className="h-8 px-3 text-xs"
                >
                  {addTaxonomyValue.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <>
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add
                    </>
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
